import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from 'react';
import type {
  CheckoutContextValue,
  PersonalInfoFormData,
  GuestInfoFormData,
  AdditionalInfoFormData,
  PricingData,
  SummaryProps,
  UnitConfiguration,
  AddonConfiguration,
  PartnerInfo,
  KioskSetting,
  StripeState,
} from './types';

const CheckoutContext = createContext<CheckoutContextValue | undefined>(
  undefined
);

interface CheckoutProviderProps {
  children: ReactNode;

  bookingRequestId: string;
  tourId?: number;
  units: UnitConfiguration[];
  selectedAddons: AddonConfiguration[];

  pricingData: PricingData;
  summaryProps: SummaryProps;

  total: number;
  currency: string;
  zeroPricedTour: boolean | null;
  fullyDiscounted: boolean;
  paidInCash: boolean;

  isBookingInvoice: boolean;
  isLiveBooking: boolean;
  isAffiliate: boolean;
  isUnpaidBooking: boolean;
  isPayLaterEnabled: boolean;
  isOutsidePayLaterCutoff: boolean;
  isRsvpEnabled: boolean;

  phoneMandatory: boolean;
  isGuestInfoCollectionEnabled: boolean;
  isGuestOfGuestEnabled: boolean;
  guestOfGuestMaxGuests: number;

  partnerInfo: PartnerInfo;
  kioskSetting?: KioskSetting;
  managedAccountToken?: string;
  stripeTheme?: any;

  tourSettings?: any;
  includesTransport: boolean;

  i18nLang: string;
  i18nList: string[];

  willExpireOn?: string;
  showTranslate: boolean;

  privacyPolicyLink: string;
  privacyPolicyLinkText: string;
  hidePolicyLinks: boolean;

  discountApplied: boolean;
  discountLoading: boolean;
  discountErrors?: any;
  isGA4Enabled: boolean;
  loading: boolean;
}

export const CheckoutProvider: React.FC<CheckoutProviderProps> = ({
  children,
  bookingRequestId,
  tourId,
  units,
  selectedAddons,
  pricingData,
  summaryProps,
  total,
  currency,
  zeroPricedTour,
  fullyDiscounted,
  paidInCash,
  isBookingInvoice,
  isLiveBooking,
  isAffiliate,
  isUnpaidBooking,
  isPayLaterEnabled,
  isOutsidePayLaterCutoff,
  isRsvpEnabled,
  phoneMandatory,
  isGuestInfoCollectionEnabled,
  isGuestOfGuestEnabled,
  guestOfGuestMaxGuests,
  partnerInfo,
  kioskSetting,
  managedAccountToken,
  stripeTheme,
  tourSettings,
  includesTransport,
  i18nLang,
  i18nList,
  willExpireOn,
  showTranslate,
  privacyPolicyLink,
  privacyPolicyLinkText,
  hidePolicyLinks,
  discountApplied,
  discountLoading,
  discountErrors,
  isGA4Enabled,
  loading,
}) => {
  const [currentPage, setCurrentPage] = useState<1 | 2>(1);
  const [pageOneData, setPageOneData] = useState<Partial<
    PersonalInfoFormData & GuestInfoFormData & AdditionalInfoFormData
  > | null>(null);
  const [bookerUnitId, setBookerUnitId] = useState<number | null>(null);
  const [stripeState, setStripeState] = useState<StripeState>({
    fieldsCompleted: [],
  });

  const value = useMemo<CheckoutContextValue>(
    () => ({
      currentPage,
      setCurrentPage,
      pageOneData,
      setPageOneData,

      bookingRequestId,
      tourId,
      units,
      selectedAddons,

      pricingData,
      summaryProps,

      total,
      currency,
      zeroPricedTour,
      fullyDiscounted,
      paidInCash,

      isBookingInvoice,
      isLiveBooking,
      isAffiliate,
      isUnpaidBooking,
      isPayLaterEnabled,
      isOutsidePayLaterCutoff,
      isRsvpEnabled,

      phoneMandatory,
      isGuestInfoCollectionEnabled,
      isGuestOfGuestEnabled,
      guestOfGuestMaxGuests,

      partnerInfo,
      kioskSetting,
      managedAccountToken,
      stripeTheme,

      tourSettings,
      includesTransport,

      i18nLang,
      i18nList,

      willExpireOn,
      showTranslate,

      privacyPolicyLink,
      privacyPolicyLinkText,
      hidePolicyLinks,

      bookerUnitId,
      setBookerUnitId,

      stripeState,
      setStripeState,

      discountApplied,
      discountLoading,
      discountErrors,

      isGA4Enabled,
      loading,
    }),
    [
      currentPage,
      pageOneData,
      bookingRequestId,
      tourId,
      units,
      selectedAddons,
      pricingData,
      summaryProps,
      total,
      currency,
      zeroPricedTour,
      fullyDiscounted,
      paidInCash,
      isBookingInvoice,
      isLiveBooking,
      isAffiliate,
      isUnpaidBooking,
      isPayLaterEnabled,
      isOutsidePayLaterCutoff,
      isRsvpEnabled,
      phoneMandatory,
      isGuestInfoCollectionEnabled,
      isGuestOfGuestEnabled,
      guestOfGuestMaxGuests,
      partnerInfo,
      kioskSetting,
      managedAccountToken,
      stripeTheme,
      tourSettings,
      includesTransport,
      i18nLang,
      i18nList,
      willExpireOn,
      showTranslate,
      privacyPolicyLink,
      privacyPolicyLinkText,
      hidePolicyLinks,
      bookerUnitId,
      stripeState,
      discountApplied,
      discountLoading,
      discountErrors,
      isGA4Enabled,
      loading,
    ]
  );

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = (): CheckoutContextValue => {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
};
